import React, { useState } from 'react';
import { BsFillEyeFill } from 'react-icons/bs';
import Modal from 'react-modal';
import { format } from 'date-fns';
import { Form } from '@unform/web';

import { Input } from '~/components/Forms';
import Button from '~/components/Button';
import ApiClient from '~/utils/ApiClient';
import { showModalMessage } from '~/services/notification';
import { formatCnpj } from '~/functions/document';

import {
  ModalContent,
  overlayStyle,
  contentStyle,
  BoxHeader,
  Wrap,
} from '~/components/Admin/Home/ListPageUsuarios/styles';

function formatDate(value, pattern = 'dd/MM/yyyy') {
  return value ? format(new Date(value), pattern) : '';
}

/**
 * Conta sem cadastro PF nem PJ: só existe o login. O e-mail é o único dado
 * editável; o resto é histórico do sistema e fica desabilitado (cinza) para
 * não parecer campo de edição. Os names levam o prefixo "sem_cadastro_"
 * porque o Input usa o name como id e o filtro da página já tem um campo de
 * e-mail.
 */
function ListPageUsuarioSemCadastro({ data, onSaved }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(data.email);
  const [saving, setSaving] = useState(false);

  const trimmedEmail = email.trim();
  const canSave = !saving && !!trimmedEmail && trimmedEmail !== data.email;

  function handleOpen() {
    setEmail(data.email);
    setOpen(true);
  }

  async function handleSubmit() {
    setSaving(true);
    let response;
    try {
      response = await ApiClient.doRequest({
        uri: `user-sem-cadastro/${data.uuid}`,
        method: 'PUT',
        body: { email: trimmedEmail },
      });
    } catch {
      response = null;
    } finally {
      setSaving(false);
    }

    if (response?.status !== 200) {
      await showModalMessage({
        type: 'error',
        text:
          response?.data?.error?.message ||
          'Não foi possível atualizar o usuário',
      });
      return;
    }

    // A troca invalida os links de senha já enviados, mas uma sessão aberta
    // pode seguir ativa até o token expirar (ver
    // UserService::changeEmailWithoutRegistration). O aviso diz só isso,
    // para o admin orientar quem pediu a correção sem prometer mais.
    await showModalMessage({
      type: 'success',
      text:
        'Usuário atualizado com sucesso. Links de senha enviados ao e-mail ' +
        'anterior deixaram de valer: para entrar, a pessoa deve usar ' +
        '"Esqueci minha senha" com o novo e-mail.',
      timer: 8,
    });
    setOpen(false);
    onSaved(trimmedEmail);
  }

  function renderDetails() {
    return (
      <>
        <BoxHeader>
          <span className="boxText">Dados da conta</span>
        </BoxHeader>
        <Form onSubmit={handleSubmit}>
          <Wrap>
            <Input
              type="email"
              name="sem_cadastro_email"
              label="E-mail"
              value={email}
              onChange={e => setEmail(e.target.value)}
              isRequired={!trimmedEmail}
            />
          </Wrap>
          <Wrap>
            <Input
              name="sem_cadastro_created_at"
              label="Conta criada em"
              value={formatDate(data.created_at, 'dd/MM/yyyy HH:mm')}
              disabled
            />
            <Input
              name="sem_cadastro_updated_at"
              label="Última atualização"
              value={formatDate(data.updated_at, 'dd/MM/yyyy HH:mm')}
              disabled
            />
          </Wrap>
          <Wrap>
            <Input
              name="sem_cadastro_last_login_at"
              label="Último acesso"
              value={
                data.last_login_at
                  ? formatDate(data.last_login_at, 'dd/MM/yyyy HH:mm')
                  : 'Nunca acessou'
              }
              disabled
            />
            <Input
              name="sem_cadastro_login_count"
              label="Total de acessos"
              value={data.login_count}
              disabled
            />
          </Wrap>
          <Wrap>
            <Input
              name="sem_cadastro_roles"
              label="Perfis"
              value={data.roles.length ? data.roles.join(', ') : 'Nenhum'}
              disabled
            />
          </Wrap>

          {data.companies.length > 0 && (
            <>
              <BoxHeader>
                <span className="boxText">Representante das empresas</span>
              </BoxHeader>
              {data.companies.map((company, index) => (
                <Wrap key={company.uuid}>
                  <Input
                    name={`sem_cadastro_company_${index}`}
                    label="Razão Social"
                    value={company.company_name}
                    disabled
                  />
                  <Input
                    name={`sem_cadastro_cnpj_${index}`}
                    label="CNPJ"
                    value={formatCnpj(company.cnpj)}
                    disabled
                  />
                  <Input
                    name={`sem_cadastro_cargo_${index}`}
                    label="Cargo"
                    value={company.cargo || ''}
                    disabled
                  />
                </Wrap>
              ))}
            </>
          )}

          {/*
            O Button não tem estilo de desabilitado (a cor não muda), então o
            estado vai na cor, no cursor e na dica ao lado.
          */}
          <BoxHeader style={{ marginTop: 20, alignItems: 'center' }}>
            <Button
              label={saving ? 'Salvando...' : 'Salvar'}
              btType={canSave ? '#26A2B1' : '#BDBDBD'}
              type="submit"
              disabled={!canSave}
              style={{ cursor: canSave ? 'pointer' : 'not-allowed' }}
              full
              width="157px"
            />
            {!saving && !canSave && (
              <span style={{ fontSize: 14, lineHeight: '20px' }}>
                Altere o e-mail para salvar
              </span>
            )}
          </BoxHeader>
        </Form>
      </>
    );
  }

  return (
    <>
      <tr>
        <td>{formatDate(data.created_at)}</td>
        <td>{data.email}</td>
        <td className="buttons">
          <span tooltip="Mais detalhes...">
            <button type="button" onClick={handleOpen}>
              <BsFillEyeFill color="#4f4f4f" size={24} />
            </button>
          </span>
        </td>
      </tr>
      <Modal
        onRequestClose={() => setOpen(false)}
        ariaHideApp={false}
        isOpen={open}
        style={{
          overlay: overlayStyle,
          content: contentStyle,
        }}
      >
        <ModalContent>{renderDetails()}</ModalContent>
      </Modal>
    </>
  );
}

export default ListPageUsuarioSemCadastro;
